import { css } from '@emotion/react';
import { Skeleton } from '@mui/material';
import { ColDef } from 'ag-grid-community';
import { useAtomValue } from 'jotai';
import { FC, useMemo } from 'react';
import { AgTable } from '../../../components/AgTable/AgTable';
import { IFinanceRoundDataModel, isConvertibleRound } from '../../../data-models/finance-round.data-model';
import { IFundDataModel } from '../../../data-models/fund.data-model';
import {
  IBoardSeat,
  IFinancingBoard,
  IFinancingRights,
  IRoundGovernanceTerms,
} from '../../../data-models/round-governance.data-model';
import { FundDataModel } from '../../../schemas/Fund.schema';
import { normalizeFundLabel } from '../../../util/fundMatchKey';
import {
  YesNoValue,
  buildOrgFundNameSet,
  buildOrgFundsByKey,
  deriveFirmBoardSeat,
  deriveFirmBoardSeatAcross,
  deriveInformationRightsTile,
  deriveInvestorRightsTile,
  deriveInvestorRightsTileAcross,
  matchesOrgFund,
  mergeFinancingBoards,
  selectPrimaryFinancing,
  splitFirmNames,
} from '../../../util/round-governance';
import { fundsAtom, fundsByIdMapAtom, isReportingFund } from '../../../services/state/AppConfigStateJ';
import {
  useRoundBoard,
  useRoundRights,
} from '../../../services/queries-hooks/MaggieRoundGovernanceQueriesHooks';
import { colors } from '../../../theme/colors';
import { heldFundIdsJ } from '../Summary/EquityHoldings/state';
import { CollapsibleSectionCard } from './CollapsibleSectionCard';
import { Row } from './RoundOverviewCard';
import {
  gridAutoCss,
  gridHeaderEllipsisCss,
  gridHugRowsCss,
  gridOverlayMutedCss,
} from './deals-detail.styles';

/**
 * Re-exported from `util/round-governance`, where these moved when the company detail
 * panel needed them too. Kept here so this module's existing importers and tests are
 * unaffected by the move.
 */
export {
  buildOrgFundNameSet,
  deriveFirmBoardSeat,
  deriveFirmBoardSeatAcross,
  deriveInformationRightsTile,
  deriveInvestorRightsTile,
  deriveInvestorRightsTileAcross,
  matchesOrgFund,
  mergeFinancingBoards,
  selectPrimaryFinancing,
};
export type { YesNoValue };

/** A parsed financing with no stated term reads differently from no data at all. */
export function toTileDisplay(value: YesNoValue, hasFinancing: boolean): string {
  if (value != null) {
    return value;
  }
  return hasFinancing ? 'Not stated' : '–';
}

export interface IGovernanceBoardRow {
  name: string;
  seat: 'Board Member' | 'Observer';
  affiliation: string | null;
}

/**
 * Board grid rows: seats first, then observers.
 * Vacant seats are not rows (the extracted size is authorized, not seated — see boardSeatNote).
 * Observers render only when named or held by an org fund — both BE sources pad
 * unnamed observers to a count, and those rows are noise (docs rarely name them).
 * An affiliation that is itself a board person's name renders as Common: the
 * extractor emits the designating common stockholder there, not a firm. A filled deal-doc
 * seat with neither firm nor fundId reads as Common too; Aumni firmless seats are roster
 * names of unknown affiliation, not common designees.
 */
export function toBoardRows(board: IFinancingBoard | null, orgFundNames: Set<string>): IGovernanceBoardRow[] {
  if (!board) {
    return [];
  }
  const personNames = new Set(
    [...board.seats.map((seat) => seat.memberName), ...board.observers.map((observer) => observer.name)]
      .filter((name): name is string => name != null)
      .map((name) => name.trim().toLowerCase())
  );
  const toAffiliation = (firm: string | null, commonDesignee = false): string | null => {
    if (firm != null && personNames.has(firm.trim().toLowerCase())) {
      return 'Common';
    }
    if (commonDesignee) {
      return 'Common';
    }
    return firm ?? (board.source === 'AUMNI' ? 'Not provided by Aumni' : null);
  };
  // Investor-appointed seats arrive with a fundId; a filled seat with neither firm nor
  // fundId on a deal-doc board is the extractor's shape for a common-designated director.
  const isCommonDesignee = (seat: IBoardSeat) =>
    board.source === 'DEAL_DOC' &&
    seat.seatStatus === 'FILLED' &&
    !!seat.memberName?.trim() &&
    !seat.firm?.trim() &&
    seat.fundId == null;
  return [
    ...board.seats
      .filter((seat) => seat.seatStatus !== 'VACANT')
      .map<IGovernanceBoardRow>((seat) => ({
        name: seat.memberName ?? 'Not named',
        seat: 'Board Member',
        affiliation: toAffiliation(seat.firm, isCommonDesignee(seat)),
      })),
    ...board.observers
      .filter((observer) => observer.name != null || matchesOrgFund(observer.firm, orgFundNames))
      .map<IGovernanceBoardRow>((observer) => ({
        name: observer.name ?? 'Not named',
        seat: 'Observer',
        affiliation: toAffiliation(observer.firm),
      })),
  ];
}

/** What we know vs the extracted board size — a knowledge claim, never a vacancy claim. */
export function boardSeatNote(board: IFinancingBoard | null, visibleRows: number): string | null {
  if (!board || board.boardSizeIsDerived || board.totalBoardSize == null) {
    return null;
  }
  const identified = board.seats.filter((seat) => seat.seatStatus !== 'VACANT').length;
  // Only the empty state may speak for the whole grid; observer rows can still be showing.
  if (identified === 0 && visibleRows === 0) {
    return `Board size ${board.totalBoardSize} — no directors identified yet`;
  }
  return board.totalBoardSize > identified
    ? `${identified} of ${board.totalBoardSize} directors identified`
    : null;
}

function boolToYesNo(value: boolean | null | undefined): YesNoValue {
  if (value == null) {
    return null;
  }
  return value ? 'Yes' : 'No';
}

const QSBS_NO_TOKENS = ['no', 'false', 'none', 'n/a', 'not applicable'];
const QSBS_YES_TOKENS = ['yes', 'true'];
const QSBS_UNKNOWN_TOKENS = [
  'not stated',
  'not specified',
  'not addressed',
  'not available',
  'to be determined',
];

/** QSBS holds clause text: a stated clause → Yes (text via tooltip), no-token → No, unstated/unknown → null (`display` shows No, `select` keeps null for the counter). */
export function qsbsDisplay(raw: string | null | undefined): { value: YesNoValue; clause: string | null } {
  const text = raw?.trim();
  if (!text) {
    return { value: null, clause: null };
  }
  const normalized = text.toLowerCase();
  if (QSBS_NO_TOKENS.includes(normalized)) {
    return { value: 'No', clause: null };
  }
  if (QSBS_YES_TOKENS.includes(normalized)) {
    return { value: 'Yes', clause: null };
  }
  // A covenant clause is a sentence; a lone unrecognized word ("Unknown", "TBD") or a
  // known unknown-phrase is extractor noise and must not read as Yes on a tax covenant.
  if (!/\s/.test(text) || QSBS_UNKNOWN_TOKENS.includes(normalized)) {
    return { value: null, clause: null };
  }
  return { value: 'Yes', clause: text };
}

export interface IDealTerm {
  label: string;
  /** Extraction signal: null = not extracted (drives the "N of 18 extracted" count). */
  select: (terms: IRoundGovernanceTerms) => YesNoValue;
  /** Overrides the rendered text when the extraction-signal null shouldn't show as a dash. */
  display?: (terms: IRoundGovernanceTerms) => string;
  /** Hover detail (e.g. QSBS clause text); rendered as a tooltip when non-null. */
  tooltip?: (terms: IRoundGovernanceTerms) => string | null;
}

/** Deal-terms grid, column-major in the prototype's display order (3 × 6 = 18 terms). */
export const DEAL_TERM_COLUMNS: ReadonlyArray<ReadonlyArray<IDealTerm>> = [
  [
    { label: 'Protective Provisions', select: (t) => (t.protectiveSectionsReference?.trim() ? 'Yes' : null) },
    { label: 'Drag-Along', select: (t) => boolToYesNo(t.dragAlong) },
    { label: 'Pay-to-Play', select: (t) => boolToYesNo(t.payToPlay) },
    { label: 'Anti-Dilution Rights', select: (t) => boolToYesNo(t.antiDilutionRights) },
    { label: 'Redemption Rights', select: (t) => boolToYesNo(t.redemptionRights) },
    { label: 'Cumulative Dividends', select: (t) => boolToYesNo(t.cumulativeDividends) },
  ],
  [
    { label: 'ROFR & Co-Sale', select: (t) => boolToYesNo(t.rofrAndCosaleAgreement) },
    {
      label: 'Registration Rights (Preferred)',
      select: (t) => boolToYesNo(t.registrationRightsForPreferred),
    },
    { label: 'Standard Pro Rata Formulation', select: (t) => boolToYesNo(t.standardProRataFormulation) },
    {
      label: 'Pro Rata Rights (Major Investors)',
      select: (t) => boolToYesNo(t.proRataRightsForMajorInvestors),
    },
    { label: 'Pro Rata Rights (All)', select: (t) => boolToYesNo(t.proRataRightsForAll) },
    {
      label: 'Information Rights (Major Investors)',
      select: (t) => boolToYesNo(t.informationRightsForMajorInvestors),
    },
  ],
  [
    { label: 'Information Rights (All)', select: (t) => boolToYesNo(t.informationRightsForAll) },
    { label: 'Employee Vesting Protocol', select: (t) => boolToYesNo(t.employeeVestingProtocol) },
    {
      label: 'Issuer Pays Investor Counsel Fees',
      select: (t) => boolToYesNo(t.issuerPaysInvestorCounselFees),
    },
    { label: 'Required Closing Payments', select: (t) => boolToYesNo(t.requiredClosingPayments) },
    {
      label: 'QSBS Covenant',
      select: (t) => qsbsDisplay(t.qsbsCovenant).value,
      display: (t) => qsbsDisplay(t.qsbsCovenant).value ?? 'No',
      tooltip: (t) => qsbsDisplay(t.qsbsCovenant).clause,
    },
    {
      label: 'QSBS Covenant Representation',
      select: (t) => qsbsDisplay(t.qsbsRepMade).value,
      display: (t) => qsbsDisplay(t.qsbsRepMade).value ?? 'No',
      tooltip: (t) => qsbsDisplay(t.qsbsRepMade).clause,
    },
  ],
];

export const DEAL_TERM_COUNT = DEAL_TERM_COLUMNS.reduce((sum, column) => sum + column.length, 0);

/** The text a term renders: display wins over the extraction signal. */
const termText = (term: IDealTerm, terms: IRoundGovernanceTerms): string | null =>
  term.display?.(terms) ?? term.select(terms);

/** How many of the 18 grid terms render a value — the counter must agree with the rows. */
export function countExtractedTerms(terms: IRoundGovernanceTerms | null | undefined): number {
  if (!terms) {
    return 0;
  }
  return DEAL_TERM_COLUMNS.flat().filter((term) => termText(term, terms) != null).length;
}

/**
 * Org funds appearing anywhere in this round's rights or board data (Our Position
 * subtitle). Firm strings can pack several funds, so each part is matched on its own.
 * Deduped by fund — a fund cited by alias in one doc and legal name in another is one
 * fund, not two — and displayed under the fund table's configured name, never the
 * extraction's spelling of it.
 */
export function selectOurFundNames(
  rightsFinancings: IFinancingRights[] | undefined,
  boardFinancings: IFinancingBoard[] | undefined,
  funds: FundDataModel[]
): string[] {
  const fundsByKey = buildOrgFundsByKey(funds);
  const matched = new Set<FundDataModel>();
  const add = (name: string | null) => {
    for (const candidate of name != null ? [name, ...splitFirmNames(name)] : []) {
      const fund = fundsByKey.get(normalizeFundLabel(candidate));
      if (fund) {
        matched.add(fund);
      }
    }
  };
  rightsFinancings?.forEach((rights) => rights.investors.forEach((investor) => add(investor.fundName)));
  boardFinancings?.forEach((board) => {
    board.seats.forEach((seat) => add(seat.firm));
    board.observers.forEach((observer) => add(observer.firm));
  });
  return [...matched].map((fund) => fund.name);
}

const subtitleCss = css`
  margin: 0;
  font-size: 12px;
  line-height: 1.66;
  color: ${colors.text.secondary};
`;

const topGridCss = css`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 16px;
  align-items: start;
`;

const sectionCaptionCss = css`
  font-size: 0.75rem;
  font-weight: 700;
  color: ${colors.text.main};
`;

const positionBoxCss = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  border: 1px solid ${colors.neutral[80]};
  border-radius: 4px;
  background: ${colors.neutral[2]};
`;

const positionFundCss = css`
  font-size: 12px;
  line-height: 1.66;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Mirrors the term-row rhythm (12px label / 14px value) so both panels read as one.
const positionRowCss = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const positionLabelCss = css`
  font-size: 12px;
  line-height: 1.66;
  color: ${colors.text.secondary};
`;

const positionValueCss = css`
  font-size: 14px;
  line-height: 1.43;
  letter-spacing: 0.17px;
  color: ${colors.text.main};
  font-weight: 500;
`;

const termsAreaCss = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const termsHeaderCss = css`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;

const termsCounterCss = css`
  font-size: 12px;
  line-height: 1.66;
  color: ${colors.text.secondary};
`;

const seatNoteCss = css`
  ${termsCounterCss};
  margin: 4px 0 0;
`;

const termsGridCss = css`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
`;

const termsColCss = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-right: 12px;
  border-right: 1px solid ${colors.border.card};

  &:last-of-type {
    border-right: none;
    padding-right: 0;
  }
`;

// body2 per Figma 2158:3820: regular weight, 4px above the grid.
const tableLabelCss = css`
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.43;
  letter-spacing: 0.17px;
  color: ${colors.text.main};
`;

const boardColumnDefs: ColDef<IGovernanceBoardRow>[] = [
  { headerName: 'Name', field: 'name', flex: 2, minWidth: 160, headerTooltip: 'Name' },
  { headerName: 'Seat', field: 'seat', flex: 1, minWidth: 120, headerTooltip: 'Seat' },
  { headerName: 'Affiliation', field: 'affiliation', flex: 2, minWidth: 160, headerTooltip: 'Affiliation' },
];

/** Reporting funds still holding the company, resolved to names. Vehicles never name a position. */
export function selectHoldingFundNames(
  heldFundIds: number[],
  fundsById: Map<number, IFundDataModel>
): string[] {
  const names = new Set<string>();
  for (const fundId of heldFundIds) {
    const fund = fundsById.get(fundId);
    if (fund && isReportingFund(fund)) {
      names.add(fund.name);
    }
  }
  return [...names].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

/**
 * Held funds win. The round's own governance names stand in only when the ledger has nothing to say;
 * a ledger that lists lots and holds none of them is a fully exited position, not a gap.
 */
export function selectOurPositionFunds(
  holdingFundNames: string[],
  hasLots: boolean,
  roundFundNames: () => string[]
): string[] {
  if (holdingFundNames.length > 0) {
    return holdingFundNames;
  }
  return hasLots ? [] : roundFundNames();
}

interface RoundGovernanceSectionProps {
  round: IFinanceRoundDataModel;
  companyId: number;
}

/**
 * Governance & Investor Rights section of the Deals-tab detail pane: stat tiles,
 * board of directors, and governance-term chips parsed from the round's deal docs.
 * Without parsed docs the tiles show dashes and the board grid its empty overlay.
 */
export const RoundGovernanceSection: FC<RoundGovernanceSectionProps> = ({ round, companyId }) => {
  const { data: boardData, isLoading: boardLoading } = useRoundBoard(round.id);
  const { data: rightsData, isLoading: rightsLoading } = useRoundRights(round.id);
  const funds = useAtomValue(fundsAtom);
  const fundsById = useAtomValue(fundsByIdMapAtom);
  const { fundIds: heldFundIds, hasLots, isLoading: positionLoading } = useAtomValue(heldFundIdsJ(companyId));
  // The firm can hold a seat or rights through a fund that did not buy into this round.
  const holdingFundNames = useMemo(
    () => selectHoldingFundNames(heldFundIds, fundsById),
    [heldFundIds, fundsById]
  );

  if (boardLoading || rightsLoading) {
    return <Skeleton variant='rectangular' height={140} />;
  }

  return (
    <RoundGovernanceSectionView
      boardFinancings={boardData?.financings}
      rightsFinancings={rightsData?.financings}
      funds={funds}
      position={{ holdingFundNames, hasLots, loading: positionLoading }}
      defaultCollapsed={isConvertibleRound(round)}
    />
  );
};

/** The ledger's view of our holding; the container resolves it, a preview supplies its own. */
export interface IGovernancePosition {
  holdingFundNames: string[];
  hasLots: boolean;
  loading: boolean;
}

export interface RoundGovernanceSectionViewProps {
  boardFinancings: IFinancingBoard[] | undefined;
  rightsFinancings: IFinancingRights[] | undefined;
  funds: FundDataModel[];
  position: IGovernancePosition;
  defaultCollapsed?: boolean;
}

/** Rendering half of the section, so a preview can feed it sample data instead of the round queries. */
export const RoundGovernanceSectionView: FC<RoundGovernanceSectionViewProps> = ({
  boardFinancings,
  rightsFinancings,
  funds,
  position,
  defaultCollapsed,
}) => {
  // Position tiles OR across every financing of the round — each parsed doc is a
  // partial extraction. The board grid renders a merged board (display only), and
  // Deal Terms keep the primary financing: term values across docs can conflict,
  // while boolean position facts are safe to OR.
  const orgFundNames = useMemo(() => buildOrgFundNameSet(funds), [funds]);
  const board = useMemo(() => mergeFinancingBoards(boardFinancings, funds), [boardFinancings, funds]);
  const rights = selectPrimaryFinancing(rightsFinancings);
  const boardRows = useMemo(() => toBoardRows(board, orgFundNames), [board, orgFundNames]);
  const seatNote = boardSeatNote(board, boardRows.length);
  const hasFinancing = board !== null || rights !== null;

  const positionRows: { label: string; value: YesNoValue }[] = [
    { label: 'Firm Board Seat', value: deriveFirmBoardSeatAcross(boardFinancings, orgFundNames) },
    {
      label: 'Major Investor Rights',
      value: deriveInvestorRightsTileAcross(rightsFinancings, orgFundNames, 'isMajorInvestor'),
    },
    { label: 'Information Rights', value: deriveInformationRightsTile(rightsFinancings, orgFundNames) },
    {
      label: 'Pro Rata Rights',
      value: deriveInvestorRightsTileAcross(rightsFinancings, orgFundNames, 'hasProRataRights'),
    },
  ];

  const ourFundNames = selectOurPositionFunds(position.holdingFundNames, position.hasLots, () =>
    selectOurFundNames(rightsFinancings, boardFinancings, funds)
  );
  const extractedCount = countExtractedTerms(rights?.terms);

  return (
    <CollapsibleSectionCard
      title='Governance & Investor Rights'
      isEmpty={board === null && rights === null}
      emptyAnnotation='No data extracted'
      subtitle={
        <p css={subtitleCss}>
          {(board ?? rights)?.source === 'AUMNI' ? 'From Aumni' : 'Extracted from uploaded round documents'}
        </p>
      }
      defaultCollapsed={defaultCollapsed}
    >
      <div css={topGridCss}>
        <div css={positionBoxCss}>
          <span css={sectionCaptionCss}>Our Position</span>
          {position.loading ? (
            <Skeleton variant='text' width={140} css={positionFundCss} />
          ) : (
            <span css={positionFundCss} title={ourFundNames.length > 0 ? ourFundNames.join(', ') : undefined}>
              {ourFundNames.length > 1 ? 'Held Through Multiple Funds' : (ourFundNames[0] ?? '–')}
            </span>
          )}
          {positionRows.map((row) => (
            <div css={positionRowCss} key={row.label}>
              <span css={positionLabelCss}>{row.label}</span>
              <span css={positionValueCss}>{toTileDisplay(row.value, hasFinancing)}</span>
            </div>
          ))}
        </div>
        <div css={termsAreaCss}>
          <div css={termsHeaderCss}>
            <span css={sectionCaptionCss}>Deal Terms</span>
            <span css={termsCounterCss}>
              {extractedCount} of {DEAL_TERM_COUNT} extracted
            </span>
          </div>
          <div css={termsGridCss}>
            {DEAL_TERM_COLUMNS.map((column) => (
              <div css={termsColCss} key={column[0].label}>
                {column.map((term) => {
                  const clause = rights ? (term.tooltip?.(rights.terms) ?? null) : null;
                  const shown = rights ? (termText(term, rights.terms) ?? '–') : '–';
                  return (
                    <Row key={term.label} label={term.label} value={shown} exact={clause ?? undefined} />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div>
        <h4 css={tableLabelCss}>Board of Directors</h4>
        <div
          css={[
            gridAutoCss,
            gridHeaderEllipsisCss,
            gridOverlayMutedCss,
            boardRows.length > 0 && gridHugRowsCss,
          ]}
        >
          <AgTable
            columnDefs={boardColumnDefs}
            rowData={boardRows}
            domLayout='autoHeight'
            defaultColDef={{
              flex: 1,
              suppressSizeToFit: false,
              wrapHeaderText: false,
              autoHeaderHeight: false,
            }}
            rowGroupPanelShow='never'
            overlayNoRowsTemplate={
              seatNote ??
              'Board composition not yet confirmed — awaiting IRA / Voting / ROFR or A&amp;R Charter.'
            }
          />
        </div>
        {seatNote && boardRows.length > 0 && <p css={seatNoteCss}>{seatNote}</p>}
      </div>
    </CollapsibleSectionCard>
  );
};
